import type { Dispatch } from 'react';
import { setItemToStore } from '@/helpers/utils';

/** Manual base version in git. Build injects VITE_APP_VERSION (see vite-plugin-app-version). */
export const APP_VERSION_BASE = '6.6.6';
export const version =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || APP_VERSION_BASE;

/** Public changelog docs (replaces in-app /change-log page). */
export const CHANGELOG_DOCS_URL = 'https://mizfa.tools/docs/changelog/';
export const CHANGELOG_LATEST_API_URL =
  'https://mizfa.tools/api/changelog/latest.json';
export const navbarBreakPoint = 'xl';
export const topNavbarBreakpoint = 'lg';

export const themeVariants = ['light', 'dark'] as const;



export type Theme = 'light' | 'dark' | 'auto';
export type NavbarPosition = 'vertical' | 'top' | 'combo' | 'double-top';
export type NavbarStyle = 'transparent' | 'inverted' | 'card' | 'vibrant';

export type Settings = {
  isFluid: boolean;
  isRTL: boolean;
  isDark: boolean;
  themeVariant: string;
  theme: Theme;
  navbarPosition: NavbarPosition;
  showBurgerMenu: boolean;
  currency: string;
  isNavbarVerticalCollapsed: boolean;
  navbarStyle: NavbarStyle;
};

export type ConfigState = Settings & {
  disabledNavbarPosition: NavbarPosition[];
  showSettingPanel: boolean;
  navbarCollapsed: boolean;
  openAuthModal: boolean;
};

export const settings: Settings = {
  isFluid: true,
  isRTL: true,
  isDark: true,
  themeVariant: 'primary',
  theme: 'dark',
  navbarPosition: 'vertical',
  showBurgerMenu: false,
  currency: '$',
  isNavbarVerticalCollapsed: false,
  navbarStyle: 'transparent'
};

export default { version, navbarBreakPoint, topNavbarBreakpoint, settings };

export type ConfigAction =
  | {
      type: 'SET_CONFIG';
      payload: {
        key: keyof ConfigState;
        value: ConfigState[keyof ConfigState];
        setInStore?: boolean;
      };
    }
  | { type: 'REFRESH' }
  | { type: 'RESET' };

export const configReducer = (
  state: ConfigState,
  action: ConfigAction
): ConfigState => {
  switch (action.type) {
    case 'SET_CONFIG': {
      const { payload } = action;
      if (payload.setInStore) {
        setItemToStore(payload.key, payload.value);
      }
      return {
        ...state,
        [payload.key]: payload.value
      };
    }
    case 'REFRESH':
      return {
        ...state
      };
    case 'RESET':
      localStorage.clear();
      document.documentElement.setAttribute('data-bs-theme', settings.theme);
      document.documentElement.setAttribute(
        'data-theme-variant',
        settings.themeVariant
      );
      return {
        ...state,
        ...settings
      };
    default:
      return state;
  }
};

export type ConfigDispatch = Dispatch<ConfigAction>;

export type AppContextValue = {
  config: ConfigState;
  setConfig: <K extends keyof ConfigState>(
    key: K,
    value: ConfigState[K]
  ) => void;
  configDispatch: ConfigDispatch;
  changeTheme: (theme: Theme) => void;
  changeThemeVariant: (theme: string) => void;
  isFullscreen: boolean;
};
